import { Link } from "react-router-dom";
import LearningExplorer from "./LearningExplorer";
import type { CategoryPageData } from "@/data/content";
import PageShell from "./PageShell";
import SiteCTA from "./SiteCTA";
import RelatedLinks from "./RelatedLinks";
import Reveal from "@/components/base/Reveal";
import {useSiteSettingsContext} from "@/hooks/useSiteSettingsContext";

export default function CategoryPage({ data }: { data: CategoryPageData }) {
  const { settings } = useSiteSettingsContext();
  const name = settings.siteName || "DersDinle";
  const t = (s: string) => s.replace(/\{siteName\}/g, name);
  const crumbs = [
    { label: "Ana Sayfa", href: "/" },
    { label: data.crumb },
  ];

  if (["egitimler", "soru-bankasi", "deneme-sinavlari"].includes(data.slug)) return <PageShell crumbs={crumbs}>
    <section className="bg-primary-50 px-5 py-12"><div className="mx-auto max-w-6xl"><p className="text-xs font-bold tracking-widest text-primary-700">DERS DERS, ADIM ADIM</p><h1 className="mt-4 text-3xl md:text-5xl font-bold">{data.slug === "deneme-sinavlari" ? "Her dersin kendi sınav bölümü." : data.slug === "egitimler" ? "Öğrenme kütüphaneni keşfet." : "Sınıfına uygun sorularla çalış."}</h1><p className="mt-5 max-w-3xl text-lg text-foreground-600">{data.slug === "deneme-sinavlari" ? "Türkçe, Matematik ve diğer derslerin soru sayılarını ayrı ayrı belirle. Süreli kitapçığını tamamla; doğru, yanlış ve boşlarını ders ders incele." : "Sınıfını veya KPSS hedefini seç. Dersleri filtrele, konu özetini oku ve örneği adım adım incele. Alıştırmalara öğrenci hesabından devam et."}</p><Link to="/ogrenci/soru-bankasi/test?count=25&duration=60" className="mt-6 inline-flex rounded-xl bg-primary-600 px-6 py-3 font-bold text-white">Sınav bölümlerimi seç →</Link></div></section>
    {data.slug === "deneme-sinavlari" && <section className="mx-auto max-w-6xl px-5 py-8 grid gap-4 md:grid-cols-3">{[["01 · Dersini seç", "Hesabındaki sınıfa veya sınav hedefine uygun derslerden seç. Okul öncesinde sınav yerine oyun etkinlikleri açılır."],["02 · Kitapçığını oluştur", "Hazır soru sayısı yeterliyse Matematik 25 + Türkçe 25 gibi bölümler kur. Yetersiz içerik tekrar sorularla tamamlanmaz."],["03 · Sonucunu incele", "Ders sekmeleri ve optik formla ilerle. Çalışma sınavında yanlışlar doğruları götürmez; resmî sınav puanı hesaplanmaz."]].map(([title,text]) => <article key={title} className="rounded-2xl border p-6"><h2 className="font-bold text-lg">{title}</h2><p className="mt-3 text-sm leading-6 text-foreground-600">{text}</p></article>)}</section>}
    <LearningExplorer />
  </PageShell>;
  return (
    <PageShell crumbs={crumbs}>
      {/* Hero */}
      <section className="bg-background-100 px-4 md:px-6 lg:px-10 py-14 md:py-20 border-b border-background-200">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <span className="inline-flex items-center gap-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-primary-700">
              <span className="w-5 h-px bg-accent-500" aria-hidden="true"></span>
              {name}
            </span>
            <h1 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground-950 leading-tight max-w-2xl">
              {data.title}
            </h1>
            <p className="mt-5 text-base md:text-lg text-foreground-600 leading-relaxed max-w-2xl">
              {t(data.intro)}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Kimler için */}
      <section className="bg-background-50 px-4 md:px-6 lg:px-10 py-14 md:py-20">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          <div className="lg:col-span-4">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 tracking-tight">
              Kimler için?
            </h2>
          </div>
          <div className="lg:col-span-8">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {data.audience.map((a) => (
                <li key={a} className="flex items-center gap-3 text-foreground-800">
                  <span className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 shrink-0">
                    <i className="ri-check-line text-sm" aria-hidden="true"></i>
                  </span>
                  <span className="text-sm md:text-base">{t(a)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Nasıl çalışır */}
      <section className="bg-background-100 px-4 md:px-6 lg:px-10 py-14 md:py-20">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 tracking-tight">
            Nasıl çalışır?
          </h2>
          <ol className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
            {data.steps.map((step, i) => (
              <li
                key={step.title}
                className="relative p-6 rounded-lg border border-background-200 bg-background-50"
              >
                <span className="font-heading text-sm font-bold text-primary-600">
                  Adım {i + 1}
                </span>
                <h3 className="mt-2 font-heading font-semibold text-base text-foreground-950">
                  {t(step.title)}
                </h3>
                <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">
                  {t(step.text)}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Alanlar */}
      <section className="bg-background-50 px-4 md:px-6 lg:px-10 py-14 md:py-20">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 tracking-tight">
            Neler var?
          </h2>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {data.areas.map((area) => (
              <div
                key={area.title}
                className="rounded-lg border border-background-200 bg-background-50 p-6"
              >
                <span className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className={`${area.icon} text-xl`} aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 font-heading font-semibold text-base text-foreground-950">
                  {t(area.title)}
                </h3>
                <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">
                  {t(area.text)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Rota ilişkisi */}
      <section className="bg-background-100 px-4 md:px-6 lg:px-10 py-14 md:py-20">
        <div className="max-w-6xl mx-auto">
          <div className="rounded-lg border border-primary-200 bg-primary-50/50 p-6 md:p-8">
            <div className="flex items-start gap-4">
              <span className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-500 text-background-50 shrink-0">
                <i className="ri-route-line text-xl" aria-hidden="true"></i>
              </span>
              <div>
                <h2 className="font-heading font-semibold text-base md:text-lg text-foreground-950">
                  Kişisel rotanla ilişkisi
                </h2>
                <p className="mt-2 text-sm md:text-base text-foreground-700 leading-relaxed">
                  {t(data.routeNote)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* İlgili sayfalar */}
      <section className="bg-background-50 px-4 md:px-6 lg:px-10 py-14 md:py-20">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 tracking-tight">
            İlgili sayfalar
          </h2>
          <div className="mt-6">
            <RelatedLinks items={data.related} />
          </div>
        </div>
      </section>

      <SiteCTA />
    </PageShell>
  );
}